import React from 'react'
import { ExistingMember, PolicyRulesJson, FlexiItem } from '../types'

interface Props {
  members: ExistingMember[]
  policyRulesJson?: PolicyRulesJson | null
}

const formatCurrency = (amount: number): string => '₹' + amount.toLocaleString('en-IN')

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '—'
  try {
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return dateStr
  }
}

const ReadOnlyView: React.FC<Props> = ({ members, policyRulesJson }) => {
  if (!members || members.length === 0) {
    return <div className='text-muted text-center py-6'>No enrollment data found.</div>
  }

  const selfMember = members.find(
    (m) => m.Relation === 'Self' || m.Relation === 'self'
  )

  // Resolve flexi item from policy JSON by matching FlexiName
  const selectedFlexiItem: FlexiItem | null = (() => {
    if (!selfMember?.FlexiName || !policyRulesJson?.flexi?.items) return null
    return (
      policyRulesJson.flexi.items.find(
        (i) => i.displayName === selfMember.FlexiName
      ) ?? null
    )
  })()

  const hasTopUpOrFlexi = !!(selfMember?.TopupName || selfMember?.FlexiName)
  const hasPremiumSection = selfMember && (selfMember.TotalPremiumPayable != null || hasTopUpOrFlexi)

  return (
    <div className='row'>
      {/* ── Left Column: Family + Flexi ─────────────────────── */}
      <div className={hasPremiumSection ? 'col-lg-8' : 'col-12'}>
        {/* Sum Insured Banner */}
        {selfMember?.BaseSumInsured != null && selfMember.BaseSumInsured > 0 && (
          <div className='d-flex align-items-center justify-content-between rounded p-3 mb-4'
            style={{ backgroundColor: '#f0f7ff', border: '1px solid #d0e3ff' }}>
            <div>
              <span className='text-muted fs-8'>Base Sum Insured</span>
              <div className='fw-bolder fs-5 text-primary'>
                {formatCurrency(selfMember.BaseSumInsured)}
              </div>
            </div>
            {selfMember.LivesCharged != null && (
              <div className='text-end'>
                <span className='text-muted fs-8'>Lives Covered</span>
                <div className='fw-bolder fs-5'>{selfMember.LivesCharged}</div>
              </div>
            )}
          </div>
        )}

        {/* Members Table */}
        <div className='mb-5'>
          <h6 className='fw-bolder fs-6 mb-3'>Family Members</h6>
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle fs-7'>
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th>Name</th>
                  <th>Relation</th>
                  <th>DOB</th>
                  <th>Gender</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.Id}>
                    <td className='fw-bold'>{member.Name}</td>
                    <td>{member.Relation}</td>
                    <td>{formatDate(member.Dob)}</td>
                    <td>{member.Gender}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Flexi Coverage Table (compact) */}
        {selectedFlexiItem && selectedFlexiItem.coverageDetails && selectedFlexiItem.coverageDetails.length > 0 && (
          <div className='mb-5'>
            <h6 className='fw-bolder fs-6 mb-3'>
              Flexi Benefit — {selfMember?.FlexiName}
            </h6>
            <div className='d-inline-block'>
              <div className='card card-bordered shadow-sm'>
                <div className='card-body p-0'>
                  <table className='table table-row-bordered table-row-gray-200 align-middle fs-7 mb-0'>
                    <thead>
                      <tr className='bg-light'>
                        <th className='ps-4 py-2 fw-bolder'>Feature</th>
                        <th className='text-center py-2 fw-bolder text-success pe-4'>
                          {selectedFlexiItem.displayName}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedFlexiItem.sumInsuredLabel && (
                        <tr>
                          <td className='ps-4 py-2 fw-bold'>Sum Insured</td>
                          <td className='text-center py-2 fw-bolder text-primary pe-4'>
                            {selectedFlexiItem.sumInsuredLabel}
                          </td>
                        </tr>
                      )}
                      <tr className='bg-gray-100'>
                        <td className='ps-4 py-2 fw-bold'>Premium</td>
                        <td className='text-center py-2 fw-bolder text-primary pe-4'>
                          {formatCurrency(selfMember?.FlexiBasePremium || selectedFlexiItem.premiumAmount)}
                        </td>
                      </tr>
                      {selectedFlexiItem.coverageDetails.map((detail, idx) => (
                        <tr key={idx}>
                          <td className='ps-4 py-2'>{detail.label}</td>
                          <td className={`text-center py-2 pe-4 ${detail.value === 'NA' || detail.value === '—' ? 'text-muted' : 'fw-bold'}`}>
                            {detail.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Flexi selected but no coverage details */}
        {selfMember?.FlexiName && !selectedFlexiItem?.coverageDetails?.length && (
          <div className='mb-5'>
            <h6 className='fw-bolder fs-6 mb-3'>Flexi Benefit</h6>
            <div className='d-flex justify-content-between align-items-center rounded p-3 bg-light-success border border-success border-dashed'>
              <span className='fw-bold'>{selfMember.FlexiName}</span>
              <span className='fw-bolder text-primary'>
                {formatCurrency(selfMember.FlexiBasePremium || 0)}
              </span>
            </div>
          </div>
        )}

        {/* Top-Up Details */}
        {selfMember?.TopupName && (
          <div className='mb-5'>
            <h6 className='fw-bolder fs-6 mb-3'>
              Top-Up — {selfMember.TopupName}
            </h6>
            <div className='d-inline-block'>
              <div className='card card-bordered'>
                <div className='card-body py-3 px-4'>
                  <div className='d-flex justify-content-between mb-1'>
                    <span className='text-muted fs-7 me-5'>Top-Up Sum Insured</span>
                    <span className='fw-bolder fs-7'>
                      {formatCurrency(selfMember.TopupSumInsured || 0)}
                    </span>
                  </div>
                  <div className='d-flex justify-content-between mb-1'>
                    <span className='text-muted fs-7 me-5'>Premium</span>
                    <span className='fs-7'>{formatCurrency(selfMember.TopupBasePremium || 0)}</span>
                  </div>
                  <div className='d-flex justify-content-between'>
                    <span className='text-muted fs-7 me-5'>GST</span>
                    <span className='fs-7'>{formatCurrency(selfMember.TopupGst || 0)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Right Column: Premium Summary ───────────────────── */}
      {hasPremiumSection && (
        <div className='col-lg-4'>
          <div className='sticky-top' style={{ top: '1rem' }}>
            <div className='card card-bordered shadow-sm'>
              <div className='card-header py-3 px-4'>
                <h6 className='card-title fw-bolder fs-6 mb-0'>Premium Details</h6>
              </div>
              <div className='card-body py-3 px-4'>
                {selfMember.BasePremium != null && (
                  <>
                    <div className='d-flex justify-content-between mb-1'>
                      <span className='text-muted fs-7'>Base Premium</span>
                      <span className='fs-7'>{formatCurrency(selfMember.BasePremium)}</span>
                    </div>
                    {selfMember.BaseGst != null && selfMember.BaseGst > 0 && (
                      <div className='d-flex justify-content-between mb-1'>
                        <span className='text-muted fs-8 ms-2'>GST</span>
                        <span className='fs-8'>{formatCurrency(selfMember.BaseGst)}</span>
                      </div>
                    )}
                  </>
                )}

                {selfMember.TopupName && (
                  <>
                    <div className='separator separator-dashed my-2'></div>
                    <div className='d-flex justify-content-between mb-1'>
                      <span className='text-muted fs-7'>Top-Up</span>
                      <span className='fs-7'>{formatCurrency(selfMember.TopupBasePremium || 0)}</span>
                    </div>
                    {selfMember.TopupSumInsured != null && (
                      <div className='text-muted fs-8 ms-2 mb-1'>
                        SI: {formatCurrency(selfMember.TopupSumInsured)}
                      </div>
                    )}
                    {selfMember.TopupGst != null && selfMember.TopupGst > 0 && (
                      <div className='d-flex justify-content-between mb-1'>
                        <span className='text-muted fs-8 ms-2'>GST</span>
                        <span className='fs-8'>{formatCurrency(selfMember.TopupGst)}</span>
                      </div>
                    )}
                  </>
                )}

                {selfMember.FlexiName && (
                  <>
                    <div className='separator separator-dashed my-2'></div>
                    <div className='d-flex justify-content-between mb-1'>
                      <span className='text-muted fs-7'>Flexi: {selfMember.FlexiName}</span>
                      <span className='fs-7'>{formatCurrency(selfMember.FlexiBasePremium || 0)}</span>
                    </div>
                    {selfMember.FlexiGst != null && selfMember.FlexiGst > 0 && (
                      <div className='d-flex justify-content-between mb-1'>
                        <span className='text-muted fs-8 ms-2'>GST</span>
                        <span className='fs-8'>{formatCurrency(selfMember.FlexiGst)}</span>
                      </div>
                    )}
                  </>
                )}

                <div className='separator my-3'></div>
                {selfMember.TotalGst != null && selfMember.TotalGst > 0 && (
                  <div className='d-flex justify-content-between mb-2'>
                    <span className='text-muted fs-7'>Total GST</span>
                    <span className='fs-7'>{formatCurrency(selfMember.TotalGst)}</span>
                  </div>
                )}
                <div className='d-flex justify-content-between'>
                  <span className='fw-bolder fs-5'>Total Payable</span>
                  <span className='fw-bolder fs-5 text-primary'>
                    {formatCurrency(selfMember.TotalPremiumPayable || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ReadOnlyView
